import { Building2, Handshake, Landmark, LineChart, PackageCheck, UsersRound } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const areas = [
  {
    title: "Buyers & Off-takers",
    icon: Building2,
    description: "External coffee buyers, buyer profiles, contact points, qualification status and off-take relationships.",
  },
  {
    title: "Sales & Coffee Lots",
    icon: PackageCheck,
    description: "Coffee available for sale, lot formation, quantity, quality, buyer allocation, dispatch and sales completion.",
  },
  {
    title: "Commercial Contracts",
    icon: Handshake,
    description: "Buyer contracts, off-take agreements, negotiated terms, delivery obligations and contract performance.",
  },
  {
    title: "Investor Registry",
    icon: UsersRound,
    description: "Investors and funding partners, participation type, commitments, ownership or participation terms and supporting agreements.",
  },
  {
    title: "Capital & Contributions",
    icon: Landmark,
    description: "Investor capital, funding tranches, programme contributions, committed versus received funds and designated use.",
  },
  {
    title: "Returns & Commercial Performance",
    icon: LineChart,
    description: "Sales revenue, gross margins, investor returns or distributions, retained value and commercial performance by period.",
  },
];

export default function CommercialPage() {
  return (
    <div className="space-y-6" data-testid="commercial-page">
      <PageHeader
        eyebrow="Business & investment"
        title="Commercial"
        description="Independent business workspace for coffee sales, buyers, commercial contracts and investors. Harvest operations and farmer/programme payments are intentionally excluded."
      />

      <Card className="border-primary/15 bg-primary/[0.03]">
        <CardContent className="p-5 text-sm leading-6">
          <p className="font-semibold">Commercial is now a separate domain.</p>
          <p className="mt-1 text-muted-foreground">
            Physical coffee intake belongs to Harvest. Farmer payables, programme expenditure and accounting belong to Accounts.
            Commercial will deal with what ACZON does with the coffee and capital after that: buyers, sales, contracts, investors,
            external revenue and investment performance.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {areas.map((area) => {
          const Icon = area.icon;
          return (
            <Card key={area.title} className="relative overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="size-5" />
                  </div>
                  <Badge variant="outline">Foundation</Badge>
                </div>
                <CardTitle className="pt-3 text-base">{area.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">{area.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="p-5">
          <p className="text-sm font-semibold">Commercial build order reserved</p>
          <p className="mt-1 text-sm text-muted-foreground">
            We will build this module after the current Harvest and Accounts foundations are stable. The first commercial slice should
            start with buyers/off-takers and investor registry, then sales contracts, coffee sales, capital movements and investor returns.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
